import { useCallback, useEffect, useMemo, useState } from 'react';
import { goBack, navigate, replaceRoute, resetNav } from '../../app/navigation';
import type { V2AiObservation } from '../../domain/aiObservation';
import type { V2Evidence } from '../../domain/evidence';
import type { V2Finding } from '../../domain/finding';
import { areaCompletionBlockers, displayAddress, isInspectionLocked, progressFor, type ItemStatus, type LimitedPortion, type UnableReason, type V2Area, type V2ChecklistItem } from '../../domain/inspection';
import { formalWorkflowBlocked } from '../../domain/inspectionContext';
import { FormalWorkflowGateCard } from '../inspections/FormalWorkflowGateCard';
import { formalWorkflowGateFor } from '../../state/formalWorkflow';
import { useT } from '../../i18n/i18n';
import { aiFailureMessage, areaKindLabel, floorLabelFor, resultLabel, unableReasonLabel, areaDisplayName, itemHintFor, itemTitleFor } from '../../i18n/labels';
import { markAdvancedJobViewedAction } from '../../state/aiJobsStore';
import { aiAttentionOfItem, aiStore, generateAreaReportWordingAction, observationsOfItem, observationsShownForItem, recordObservationAsOtherFindingAction, wordingLiveOf } from '../../state/aiStore';
import { rememberCurrentArea } from '../../state/areaFocus';
import type { EvidenceTarget } from '../../state/captureFlow';
import { useStore } from '../../state/createStore';
import { evidenceStore } from '../../state/evidenceStore';
import { areaScanPhotos } from '../../state/photoPool';
import { findingStore, otherFindingsOfArea, unlinkEvidenceFromFindingAction } from '../../state/findingStore';
import {
  addLimitedPortionAction,
  changeItemResultAction,
  completeAreaAction,
  type ItemResultConfirmations,
  inspectionStore,
  itemsOf,
  nextUninspectedItem,
  reopenAreaAction,
  setAreaNoteAction,
  unlinkEvidenceFromItemAction
} from '../../state/inspectionStore';
import { Banner, Button, Card, ConfirmSheet, EmptyState, FilterChips, Icon, InlineMessage, ProgressBar, SectionHeader, SegmentedControl, TextArea, TopBar, showToast } from '../../ui/components';
import { useStickyFooterClearance } from '../../ui/stickyFooter';
import { formatClock, useAutosaveText } from '../../ui/hooks';
import { Lightbox, type LightboxContext } from '../evidence/Lightbox';
import { useEvidenceCapture } from '../evidence/useEvidenceCapture';
import type { FindingPrefill } from '../findings/FindingEditor';
import { otherFindingDraftFrom } from '../../state/aiFindingDraft';
import { FindingEditorSheet } from '../findings/FindingEditorSheet';
import { FindingList } from '../findings/FindingList';
import { VoluntaryObservationSheet } from '../findings/VoluntaryObservationSheet';
import { PhotoPoolPickerSheet } from '../import/PhotoPoolPickerSheet';
import { ItemNoteSheet } from '../items/ItemNoteSheet';
import { ItemSheet } from '../items/ItemSheet';
import { AiScanCard } from './AiScanCard';
import { AiUnclassified } from './AiUnclassified';
import { PortionRemovalConfirm, type PortionRemovalRequest } from './PortionRemovalConfirm';
import { UnableReasonSheet } from './UnableReasonSheet';

// Area page (Final Owner Decision 2026-09-14 §34 — the ONE workflow):
//   AI扫描此区域 (the entry; one status line once done — CA-2)
//   → AI发现 N 项未归类 (only what the AI could not match to a row — CA-5)
//   → progress → checklist (one tap = a result; 拍照 · 照片池 · 评语 on each card; ✦ AI 有观察 when the
//     row carries an observation — the chip opens the row, never a second AI sheet — CA-3)
//     2026-10-28 (device walkthrough A2 / A4, the page was 7 screens and ~110 controls): a row that is
//     answered 无异常 / 不适用 and has nothing of the AI waiting on it (no defect reading, no question) and no
//     parts recorded as not observed is ONE line — title · result · photos — that opens the row (OC §4.6:
//     folded, never gone). Rows with no answer, a 缺陷 / 无法检查, or an AI defect reading stay full cards;
//     so does a row answered from its card during this visit, so nothing moves under the inspector's
//     finger. On a full card the title area is one tap target and the buttons start 24 pt below it.
//     The unclassified list (AiUnclassified) is one line with its count until opened in place.
//   → other findings → area report note
//   sticky: + 发现其他问题 · 生成本区报告语 · Complete / Reopen
// Pass / N/A / Defect are one tap. Unable opens the quick reason sheet. No AI result module, no
// statistics block, no accept / adopt path (CA-1 / CA-4).
type Filter = 'all' | 'notChecked' | 'issueFound' | 'checkedOk' | 'limited';

interface LightboxState {
  evidence: V2Evidence[];
  index: number;
  context: LightboxContext;
}

interface FindingEditorState {
  finding: V2Finding | null;
  prefill?: FindingPrefill;
  // 记为其他问题 on an unclassified AI observation (CA-5 §6.2): the saved finding is the inspector's;
  // the observation remembers where it went.
  observation?: V2AiObservation;
}

// Figma 05 · 中文 03: one row of four result chips, each with its own result dot (the dot is the
// tone, so no extra icons) — 无异常 · 缺陷 · 无法检查 · 不适用.
const RESULT_OPTIONS: ReadonlyArray<{ status: ItemStatus; tone: 'pass' | 'defect' | 'unable' | 'na' }> = Object.freeze([
  { status: 'checkedOk', tone: 'pass' },
  { status: 'issueFound', tone: 'defect' },
  { status: 'unableToInspect', tone: 'unable' },
  { status: 'notApplicable', tone: 'na' }
]);

export function AreaWorkspaceScreen(props: { inspectionId: string; areaId: string; itemSheetId: string | null; findingId?: string | null; advancedJobId?: string | null; focusDescriptor?: string | null }) {
  const { t, tp, locale } = useT();
  const footerRef = useStickyFooterClearance();
  const inspectionState = useStore(inspectionStore, (s) => s);
  const evidenceState = useStore(evidenceStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const aiState = useStore(aiStore, (s) => s);
  const inspection = inspectionState.inspections.find((i) => i.id === props.inspectionId) ?? null;
  const area = inspectionState.areas.find((a) => a.id === props.areaId) ?? null;
  const areaItems = useMemo(() => itemsOf(inspectionState, props.areaId), [inspectionState, props.areaId]);
  // The area's own photos plus the pool photos filed into it (2026-10-05, src/state/photoPool.ts).
  const areaEvidence = useMemo(() => areaScanPhotos(evidenceState, inspectionState, findingState, props.areaId), [evidenceState, inspectionState, findingState, props.areaId]);
  const otherFindings = useMemo(() => otherFindingsOfArea(findingState, props.areaId), [findingState, props.areaId]);
  const progress = useMemo(() => progressFor(areaItems), [areaItems]);
  const blockers = useMemo(() => areaCompletionBlockers(areaItems), [areaItems]);
  const sheetItem = props.itemSheetId !== null ? (areaItems.find((i) => i.id === props.itemSheetId) ?? null) : null;

  const [filter, setFilter] = useState<Filter>('all');
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [findingEditor, setFindingEditor] = useState<FindingEditorState | null>(null);
  const [unableFor, setUnableFor] = useState<V2ChecklistItem | null>(null);
  // 2026-10-27: 无异常 chosen on a row the AI read as a defect — asked once before it is written.
  const [clearDefectFor, setClearDefectFor] = useState<{ item: V2ChecklistItem; headline: string } | null>(null);
  const [noteFor, setNoteFor] = useState<V2ChecklistItem | null>(null);
  const [poolFor, setPoolFor] = useState<V2ChecklistItem | null>(null);
  // NY F1 R3 narrow repair (F-R3-04): the confirmation for a result that drops the recorded parts not observed.
  const [portionRemoval, setPortionRemoval] = useState<{ itemId: string; request: PortionRemovalRequest } | null>(null);
  const wordingBusy = useStore(aiStore, (s) => wordingLiveOf(s, props.areaId));
  const [voluntaryOpen, setVoluntaryOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  // 2026-10-28: the rows the inspector answered from their card during this visit stay full cards until the
  // page is left — a row folding into one line right under the finger invites the next mis-tap (A4).
  const [answeredHere, setAnsweredHere] = useState<ReadonlySet<string>>(() => new Set());
  const keepOpen = useCallback((itemId: string) => setAnsweredHere((prev) => (prev.has(itemId) ? prev : new Set(prev).add(itemId))), []);

  const saveAreaNote = useCallback((value: string) => setAreaNoteAction(props.areaId, value), [props.areaId]);
  const areaNote = useAutosaveText(area?.note ?? '', saveAreaNote);
  const nextItem = useMemo(() => (sheetItem ? nextUninspectedItem(inspectionState, props.inspectionId, sheetItem.id) : null), [inspectionState, props.inspectionId, sheetItem]);

  // This is where the inspector is: the overview's Resume and outlined card return here after the
  // app was left mid-area (src/state/areaFocus.ts; Owner device test 2026-09-12).
  const areaId = area?.id ?? null;
  useEffect(() => {
    if (areaId) rememberCurrentArea(props.inspectionId, areaId);
  }, [props.inspectionId, areaId]);

  // An advanced review reached through a notification or the Task Center (CA-9 §11.7): the row opens
  // (the route carries its item sheet) and the result is read in ③; the job is marked viewed.
  const advancedJobId = props.advancedJobId ?? null;
  useEffect(() => {
    if (!advancedJobId) return;
    void markAdvancedJobViewedAction(advancedJobId);
    replaceRoute({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: props.areaId, itemSheetId: props.itemSheetId, findingId: null, advancedJobId: null });
  }, [advancedJobId, props.inspectionId, props.areaId, props.itemSheetId]);

  // Review of an area-level ("other") finding from the findings list: open its editor sheet here.
  const reviewFindingId = props.itemSheetId === null ? (props.findingId ?? null) : null;
  useEffect(() => {
    if (!reviewFindingId) return;
    const finding = findingStore.get().findings.find((f) => f.id === reviewFindingId) ?? null;
    if (finding) setFindingEditor({ finding });
    replaceRoute({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: props.areaId, itemSheetId: null, findingId: null });
  }, [reviewFindingId, props.inspectionId, props.areaId]);

  // Keep the lightbox group live so deleting a photo updates the strip it was opened from.
  const liveLightbox = useMemo(() => {
    if (!lightbox) return null;
    const ids = new Set(lightbox.evidence.map((e) => e.id));
    const evidence = evidenceState.evidence.filter((e) => ids.has(e.id));
    return { ...lightbox, evidence };
  }, [lightbox, evidenceState]);

  if (!area || !inspection) {
    return (
      <div className="screen">
        <TopBar title={t('area.title')} onBack={goBack} />
        <EmptyState title={t('area.notFound')} body={t('area.notFoundBody')} action={<Button onClick={() => resetNav({ screen: 'inspectionList' })}>{t('inspection.backToInspections')}</Button>} />
      </div>
    );
  }

  // NY F1 Round 1 (OC-1) — the formal-workflow-continuation seam.
  const gate = formalWorkflowGateFor(inspection);
  if (formalWorkflowBlocked(gate)) {
    return (
      <div className="screen" data-testid="area-workspace-gated">
        <TopBar title={areaDisplayName(locale, area)} subtitle={displayAddress(inspection, t('inspection.noAddress'))} onBack={goBack} />
        <div className="screen-body">
          <FormalWorkflowGateCard inspection={inspection} gate={gate} />
        </div>
      </div>
    );
  }

  const locked = isInspectionLocked(inspection);
  const areaTarget: EvidenceTarget = { inspectionId: props.inspectionId, areaId: props.areaId, itemId: null, findingId: null };

  function openItem(itemId: string | null) {
    replaceRoute({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: props.areaId, itemSheetId: itemId, findingId: null });
  }

  function openLightbox(evidence: V2Evidence[], index: number, context: LightboxContext) {
    setLightbox({ evidence, index, context });
  }

  // 从检查项移除 in the viewer: the inspector's override (CA-7) — recorded on the row, never undone by the AI.
  async function handleUnlink(evidenceId: string) {
    if (!lightbox) return;
    if (lightbox.context.itemId) await unlinkEvidenceFromItemAction(lightbox.context.itemId, evidenceId);
    if (lightbox.context.findingId) await unlinkEvidenceFromFindingAction(lightbox.context.findingId, evidenceId);
    setLightbox(null);
  }

  // Next Item → (D-06): the next Uninspected item in inspection order, in this area or the next one.
  function goToNextItem() {
    if (!nextItem) return;
    if (nextItem.areaId === props.areaId) openItem(nextItem.id);
    else replaceRoute({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: nextItem.areaId, itemSheetId: nextItem.id });
  }

  async function handleComplete() {
    setMessage(null);
    const outcome = await completeAreaAction(props.areaId);
    if (!outcome.ok) {
      setMessage(tp('area.completeBlocked', outcome.blockers.length));
      setFilter('notChecked');
      return;
    }
    // CA-19: completing the area writes its report wording — in the background, so the inspector
    // moves on; the item sheets and the report show it once it lands.
    void writeWording(true);
  }

  async function writeWording(fromComplete = false) {
    const outcome = await generateAreaReportWordingAction(props.areaId);
    if (outcome.ok) {
      // rows the inspector wrote themselves are left as written — say so, so a count that looks short is explained
      if (!outcome.kept) showToast(tp('area.wordingDone', outcome.rows));
      else if (outcome.rows > 0) showToast(tp('area.wordingDoneKept', outcome.rows, { kept: outcome.kept }));
      else showToast(t('area.wordingAllKept', { kept: outcome.kept }), 'info');
    } else if (outcome.code === 'noPhotos') {
      if (!fromComplete) showToast(t('area.wordingNothing'), 'info');
    } else if (!fromComplete) setMessage(aiFailureMessage(t, outcome.code));
  }

  // Every result change goes through the central action; a result that cannot carry the recorded
  // parts not observed asks first (R3 narrow repair, F-R3-04) and the answer is passed back.
  async function applyResult(item: V2ChecklistItem, status: ItemStatus, unable?: { reasonCode: UnableReason; note: string }, confirmed: ItemResultConfirmations = {}) {
    const outcome = await changeItemResultAction(item.id, status, { ...confirmed, unable });
    if (!outcome.ok) setPortionRemoval({ itemId: item.id, request: { status, unable, portions: outcome.portions, confirmed } });
  }

  // One-tap result on the card (§10): Pass / Defect / N/A are written at once; Unable opens the quick
  // reason sheet (§15). A Defect is the row's result (CA-15) — nothing else to fill in.
  // 2026-10-27: 无异常 on a row whose AI observation concluded a defect asks first (the prefilled 缺陷 is the
  // AI's reading; overruling it is the inspector's call, taken knowingly).
  async function chooseResult(item: V2ChecklistItem, status: ItemStatus) {
    if (locked) return;
    keepOpen(item.id);
    if (status === 'unableToInspect') {
      setUnableFor(item);
      return;
    }
    if (item.status === status) return;
    if (status === 'checkedOk') {
      const defect = observationsOfItem(aiState, item.id).find((o) => o.verdict === 'defect');
      if (defect) {
        setClearDefectFor({ item, headline: defect.headline.trim() || defect.observation });
        return;
      }
    }
    await applyResult(item, status);
  }

  async function confirmUnable(reason: UnableReason, note: string) {
    const item = unableFor;
    setUnableFor(null);
    if (item) await applyResult(item, 'unableToInspect', { reasonCode: reason, note }, { confirmPortionRemoval: true });
  }

  // NY F1 Round 3 (OD-5 = A): "part of it could not be seen" from the same sheet — the row keeps its
  // result; one limited portion is recorded on it.
  async function confirmPortion(portion: LimitedPortion) {
    const item = unableFor;
    setUnableFor(null);
    if (item) await addLimitedPortionAction(item.id, portion);
  }

  function confirmPortionRemoval() {
    const pending = portionRemoval;
    setPortionRemoval(null);
    if (!pending) return;
    const item = areaItems.find((i) => i.id === pending.itemId);
    if (item) void applyResult(item, pending.request.status, pending.request.unable, { ...pending.request.confirmed, confirmPortionRemoval: true });
  }

  // 记为其他问题 on an unclassified observation (CA-5): the editor opens pre-filled with the AI text and
  // photos; what the inspector saves is the inspector's other finding. The draft is English and names one
  // professional in both the sentence and the setting (src/state/aiFindingDraft.ts, 2026-10-28).
  function recordObservationAsOther(o: V2AiObservation) {
    setFindingEditor({ finding: null, prefill: otherFindingDraftFrom(o), observation: o });
  }

  const filtered = areaItems.filter((item) => {
    switch (filter) {
      case 'all':
        return true;
      case 'notChecked':
        return item.status === 'notChecked';
      case 'issueFound':
        return item.status === 'issueFound';
      case 'checkedOk':
        return item.status === 'checkedOk';
      case 'limited':
        return item.status === 'notApplicable' || item.status === 'unableToInspect';
    }
  });

  const completed = area.completedAt !== null;
  const filterOptions: Array<{ key: Filter; label: string; count: number }> = [
    { key: 'all', label: t('area.filterAll'), count: progress.total },
    { key: 'notChecked', label: t('result.pending'), count: progress.notChecked },
    { key: 'issueFound', label: t('result.defect'), count: progress.issues },
    { key: 'checkedOk', label: t('result.pass'), count: progress.ok },
    { key: 'limited', label: t('area.filterLimited'), count: progress.notApplicable + progress.unable }
  ];

  function metaFor(item: V2ChecklistItem): string {
    const parts: string[] = [];
    if (item.status === 'unableToInspect' && item.reasonCode) parts.push(unableReasonLabel(t, item.reasonCode));
    if (item.statusReason.length > 0) parts.push(item.statusReason);
    if (item.limitedPortions.length > 0) parts.push(tp('area.limitedPortionsMeta', item.limitedPortions.length));
    if (item.inspectorNote.length > 0) parts.push(item.inspectorNote);
    return parts.join(' · ');
  }

  const pct = progress.total > 0 ? Math.round((progress.settled / progress.total) * 100) : 0;
  const contextLine = [area.floor !== null ? floorLabelFor(t, area.floor) : null, areaKindLabel(t, area.kind)].filter((s): s is string => s !== null).join(' • ');
  const areaName = areaDisplayName(locale, area);

  return (
    <div className="screen" data-testid="area-workspace">
      <TopBar title={areaName} subtitle={completed ? t('area.completedSubtitle') : contextLine} onBack={goBack} />
      <div className="screen-body">
        {locked ? (
          <div className="banner-slot">
            <Banner tone="info" icon="lock" title={t('report.lockedTitle')} body={t('report.lockedBody')} testId="area-locked" />
          </div>
        ) : null}

        <AiScanCard
          inspection={inspection}
          area={area}
          target={areaTarget}
          photos={areaEvidence}
          onOpenPhoto={(index) => openLightbox(areaEvidence, index, { itemId: null, findingId: null })}
          onViewAll={() => navigate({ screen: 'photoPool', inspectionId: props.inspectionId, focus: { kind: 'area', areaId: props.areaId } })}
          disabled={locked}
        />

        <AiUnclassified inspection={inspection} area={area} items={areaItems} locked={locked} onRecordAsOther={recordObservationAsOther} />

        <Card testId="area-progress">
          <div className="progress-card-head">
            <h2 className="section-title">{t('area.progressTitle', { name: areaName })}</h2>
            <span className="progress-pill" data-testid="area-pct">
              {t('common.percent', { n: pct })}
            </span>
          </div>
          <ProgressBar value={progress.settled} total={progress.total} />
          <div className="progress-summary">
            <span>{tp('common.itemsInspected', progress.total, { done: progress.settled })}</span>
            <span>{t('common.remaining', { n: progress.notChecked })}</span>
          </div>
        </Card>

        <section className="area-section" data-testid="checklist-card">
          <SectionHeader title={t('area.items', { name: areaName })} meta={t('area.itemsHint')} />
          <FilterChips options={filterOptions} value={filter} onChange={setFilter} />
          {filtered.length === 0 ? (
            <p className="card-note">{filter === 'notChecked' ? t('area.noPending') : t('area.noMatch')}</p>
          ) : (
            <div className="item-cards">
              {filtered.map((item) => {
                // 2026-10-28 (A6): a row with a defect reading does not also list its "no visible defect" lines.
                const rowObservations = observationsShownForItem(aiState, item.id);
                const aiHeadline = rowObservations.length > 0 ? (rowObservations.find((o) => o.verdict === 'defect') ?? rowObservations[0]).headline : null;
                if (rowFolds(item, aiAttentionOfItem(aiState, item.id)) && !answeredHere.has(item.id)) {
                  return <ItemLine key={item.id} item={item} meta={metaFor(item)} aiObservation={aiHeadline !== null} onOpen={() => openItem(item.id)} />;
                }
                return (
                  <ItemCard
                    key={item.id}
                    item={item}
                    area={area}
                    inspectionId={props.inspectionId}
                    locked={locked}
                    meta={metaFor(item)}
                    aiHeadline={aiHeadline}
                    showHint={item.status === 'notChecked' || answeredHere.has(item.id)}
                    onOpen={() => openItem(item.id)}
                    onResult={(status) => void chooseResult(item, status)}
                    onNote={() => setNoteFor(item)}
                    onPool={() => setPoolFor(item)}
                  />
                );
              })}
            </div>
          )}
        </section>

        <Card testId="other-findings">
          <SectionHeader title={t('area.otherIssues')} meta={t('area.otherIssuesHint')} />
          <FindingList findings={otherFindings} onOpen={(f) => setFindingEditor({ finding: f })} emptyText={t('area.otherFindingsEmpty')} />
          {/* OD-13 §3.5: the inspector looked at something New York does not require. */}
          {!locked ? (
            <Button variant="text" size="small" block={false} icon="visibility" onClick={() => setVoluntaryOpen(true)} testId="add-voluntary-observation">
              {t('finding.voluntaryQuick')}
            </Button>
          ) : null}
          <p className="card-note">{t('finding.voluntaryHint')}</p>
        </Card>

        <Card testId="area-note">
          <SectionHeader title={t('area.reportNote')} meta={t('area.reportNoteHint')} />
          <TextArea value={areaNote.value} onChange={areaNote.setValue} placeholder={t('area.notePlaceholder')} disabled={locked} testId="area-note-input" />
          <p className="saved-note">{areaNote.savedAt ? t('common.autosaved', { time: formatClock(areaNote.savedAt) }) : t('common.autosaveHint')}</p>
        </Card>

        <div ref={footerRef} className="sticky-actions area-sticky" data-testid="area-actions">
          {message ? <InlineMessage kind="err" text={message} /> : null}
          <Button variant="secondary" onClick={() => setFindingEditor({ finding: null })} disabled={locked} testId="add-other-finding">
            {t('area.otherIssueCta')}
          </Button>
          <Button variant="secondary" icon="psychology" onClick={() => void writeWording()} disabled={locked || wordingBusy} testId="generate-wording">
            {wordingBusy ? t('area.wordingStarted') : t('area.generateWording')}
          </Button>
          {completed ? (
            <Button variant="secondary" onClick={() => void reopenAreaAction(props.areaId)} disabled={locked} testId="reopen-area">
              {t('area.reopen')}
            </Button>
          ) : (
            <>
              {blockers.length > 0 ? <p className="blockers-note">{tp('area.blockers', blockers.length)}</p> : null}
              <Button onClick={() => void handleComplete()} disabled={blockers.length > 0 || locked} testId="complete-area">
                {t('area.complete')}
              </Button>
            </>
          )}
        </div>
      </div>

      {sheetItem ? (
        <ItemSheet
          key={sheetItem.id}
          item={sheetItem}
          area={area}
          inspection={inspection}
          inspectionId={props.inspectionId}
          itemIndex={areaItems.findIndex((i) => i.id === sheetItem.id)}
          itemCount={areaItems.length}
          locked={locked}
          onClose={() => openItem(null)}
          onOpenLightbox={openLightbox}
          onNextItem={goToNextItem}
          hasNextItem={nextItem !== null}
          focusDescriptor={props.focusDescriptor ?? null}
        />
      ) : null}

      {findingEditor ? (
        <FindingEditorSheet
          key={findingEditor.finding ? findingEditor.finding.id : findingEditor.observation ? `ai-${findingEditor.observation.id}` : 'new-area'}
          inspectionId={props.inspectionId}
          areaId={props.areaId}
          areaKind={area.kind}
          existing={findingEditor.finding}
          prefill={findingEditor.prefill}
          origin={findingEditor.observation ? 'ai' : undefined}
          locked={locked}
          onClose={() => setFindingEditor(null)}
          onSaved={findingEditor.observation ? (findingId) => void recordObservationAsOtherFindingAction((findingEditor.observation as V2AiObservation).id, findingId) : undefined}
          onOpenLightbox={(evidence, index, findingId) => openLightbox(evidence, index, { itemId: null, findingId })}
        />
      ) : null}

      {unableFor ? (
        <UnableReasonSheet
          itemTitle={itemTitleFor(locale, unableFor)}
          rowTemplateId={unableFor.templateId}
          itemStatus={unableFor.status}
          existingPortions={unableFor.limitedPortions.length}
          initialReason={unableFor.reasonCode}
          initialNote={unableFor.statusReason}
          onConfirm={(reason, note) => void confirmUnable(reason, note)}
          onConfirmPortion={(portion) => void confirmPortion(portion)}
          onClose={() => setUnableFor(null)}
        />
      ) : null}

      {portionRemoval ? <PortionRemovalConfirm request={portionRemoval.request} onConfirm={confirmPortionRemoval} onCancel={() => setPortionRemoval(null)} /> : null}

      {clearDefectFor ? (
        <ConfirmSheet
          title={t('ai.clearDefectTitle')}
          body={t('ai.clearDefectBody', { headline: clearDefectFor.headline })}
          confirmLabel={t('ai.clearDefectConfirm')}
          onConfirm={() => {
            const target = clearDefectFor.item;
            setClearDefectFor(null);
            void applyResult(target, 'checkedOk');
          }}
          onCancel={() => setClearDefectFor(null)}
        />
      ) : null}

      {voluntaryOpen ? <VoluntaryObservationSheet inspectionId={props.inspectionId} areaId={props.areaId} onClose={() => setVoluntaryOpen(false)} /> : null}

      {noteFor ? <ItemNoteSheet item={areaItems.find((i) => i.id === noteFor.id) ?? noteFor} onClose={() => setNoteFor(null)} /> : null}

      {poolFor ? <PhotoPoolPickerSheet inspectionId={props.inspectionId} itemId={poolFor.id} findingId={null} linkedIds={poolFor.evidenceIds} onClose={() => setPoolFor(null)} /> : null}

      {liveLightbox ? (
        <Lightbox
          evidence={liveLightbox.evidence}
          index={Math.min(liveLightbox.index, Math.max(0, liveLightbox.evidence.length - 1))}
          onIndexChange={(index) => setLightbox((prev) => (prev ? { ...prev, index } : prev))}
          onClose={() => setLightbox(null)}
          context={liveLightbox.context}
          onUnlink={handleUnlink}
        />
      ) : null}
    </div>
  );
}

// Whether a row folds to one line on the area page (2026-10-28, device walkthrough A2): answered 无异常 or 不适用
// AND nothing of the AI waits on it. A row with no answer, a 缺陷 or 无法检查 (its reason is part of what the
// inspector reads), parts recorded as not observed (the same New York limitation, partly — NY F1 R3), an AI
// defect reading (also on a row answered 无异常 — the inspector overruled the AI and should keep seeing it) or
// an AI question stays a full card.
export function rowFolds(item: Pick<V2ChecklistItem, 'status' | 'limitedPortions'>, aiAttention: 'defect' | 'question' | null): boolean {
  return (item.status === 'checkedOk' || item.status === 'notApplicable') && item.limitedPortions.length === 0 && aiAttention === null;
}

const LINE_TONES: Partial<Record<ItemStatus, string>> = { checkedOk: 'pass', notApplicable: 'na' };

// A folded row: ONE button — title · photo count · result — that opens the row's sheet (where the result is
// ① and can be changed at once). Same data-* as a full card, so lists and tools read both alike.
function ItemLine(props: { item: V2ChecklistItem; meta: string; aiObservation: boolean; onOpen: () => void }) {
  const { t, tp, locale } = useT();
  const { item } = props;
  const title = itemTitleFor(locale, item);
  const result = resultLabel(t, item.status);
  const photos = item.evidenceIds.length;
  return (
    <div className="item-card item-card-folded" data-testid="item-card" data-collapsed="true" data-item-status={item.status} data-name={item.title} data-ai-observation={props.aiObservation ? 'true' : 'false'} data-evidence-pending={item.evidenceSuggestions.length}>
      <button
        type="button"
        className="item-line"
        onClick={props.onOpen}
        data-testid="check-row"
        data-item-status={item.status}
        aria-label={photos > 0 ? `${title} · ${result} · ${tp('common.photosCount', photos)}` : `${title} · ${result}`}
      >
        <span className="item-line-main">
          <span className="item-line-title">{title}</span>
          {/* what the full card's meta line said — parts not observed, the N/A note, the inspector's comment — stays readable */}
          {props.meta.length > 0 ? <span className="item-line-meta">{props.meta}</span> : null}
        </span>
        {photos > 0 ? (
          <span className="item-line-photos" data-testid="item-line-photos">
            <Icon name="photo_camera" />
            {photos}
          </span>
        ) : null}
        <span className={`item-line-result item-line-result-${LINE_TONES[item.status] ?? 'na'}`} data-testid="item-line-result">
          {result}
        </span>
        <Icon name="chevron_right" className="item-line-chevron" />
      </button>
    </div>
  );
}

// One checklist card: the title area is ONE tap target for the six-block sheet — title, the check's guidance
// while the row has no answer, the meta line, the light AI line (CA-3) and the count of AI-placed photos all
// inside it (2026-10-28, A4: the title, two chips and the buttons were three targets 12 pt apart). 24 pt
// below it: one row of results, one row of quick actions (拍照 · 照片池 · 评语 — the 评语 writes the row's ONE
// inspector note), every button 44 pt tall.
function ItemCard(props: {
  item: V2ChecklistItem;
  area: V2Area;
  inspectionId: string;
  locked: boolean;
  meta: string;
  aiHeadline: string | null;
  showHint: boolean;
  onOpen: () => void;
  onResult: (status: ItemStatus) => void;
  onNote: () => void;
  onPool: () => void;
}) {
  const { t, tp, locale } = useT();
  const { item } = props;
  const capture = useEvidenceCapture({ inspectionId: props.inspectionId, areaId: props.area.id, itemId: item.id, findingId: null });
  const busy = capture.busy !== null || props.locked;
  const hint = itemHintFor(locale, item);
  return (
    <div className="item-card" data-testid="item-card" data-collapsed="false" data-item-status={item.status} data-name={item.title} data-ai-observation={props.aiHeadline !== null ? 'true' : 'false'} data-evidence-pending={item.evidenceSuggestions.length}>
      <button type="button" className="item-card-hit" onClick={props.onOpen} data-testid="check-row" data-item-status={item.status}>
        <span className="item-card-title">{itemTitleFor(locale, item)}</span>
        {/* the guidance helps choose a result; once there is one, the sheet's ⑥ keeps it (a row answered here
            keeps it until the page is left, so the buttons below do not jump up under the finger) */}
        {hint.length > 0 && props.showHint ? <span className="item-card-hint">{hint}</span> : null}
        {props.meta.length > 0 ? <span className="item-card-meta">{props.meta}</span> : null}
        {props.aiHeadline !== null ? (
          <span className="ai-chip" data-testid="item-ai-chip">
            <span className="sparkle-inline">✦</span>
            <span>{t('ai.itemHasObservation')}</span>
            <span className="ai-chip-headline">{props.aiHeadline}</span>
          </span>
        ) : null}
        {/* 2026-10-27: photos the AI placed are linked already (「AI 放的」); the line only says how many, the sheet shows which. */}
        {item.evidenceSuggestions.length > 0 ? (
          <span className="ai-chip ai-chip-pending" data-testid="item-evidence-pending-chip">
            <Icon name="photo_library" />
            <span>{tp('ai.suggestedEvidenceCount', item.evidenceSuggestions.length)}</span>
          </span>
        ) : null}
      </button>
      <div className="item-card-actions">
        <SegmentedControl
          variant="compact"
          ariaLabel={t('item.status')}
          value={item.status === 'notChecked' ? null : item.status}
          onChange={props.onResult}
          options={RESULT_OPTIONS.map((option) => ({
            key: option.status,
            label: resultLabel(t, option.status),
            tone: option.tone,
            disabled: props.locked,
            testId: `row-result-${option.status}`
          }))}
        />
        <div className="item-quick-actions" data-testid="item-quick-actions">
          <button type="button" className="quick-action" onClick={() => void capture.takePhoto()} disabled={busy} data-testid="quick-photo">
            <Icon name="photo_camera" />
            <span>{capture.busy === 'camera' ? t('photo.opening') : t('photo.cameraShort')}</span>
            {item.evidenceIds.length > 0 ? <span className="count-dot">{item.evidenceIds.length}</span> : null}
          </button>
          <button type="button" className="quick-action" onClick={props.onPool} disabled={busy} data-testid="quick-pool">
            <Icon name="perm_media" />
            <span>{t('pool.titleShort')}</span>
          </button>
          <button type="button" className={`quick-action ${item.inspectorNote.length > 0 ? 'quick-action-active' : ''}`} onClick={props.onNote} disabled={props.locked} data-testid="quick-note">
            <Icon name="mode_comment" />
            <span>{t('item.commentShort')}</span>
          </button>
        </div>
        {capture.message ? <InlineMessage kind={capture.message.kind} text={capture.message.text} /> : null}
      </div>
    </div>
  );
}
